import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { createForm } from '@components/form';
import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useConnect } from './hooks';

export const useConnectModal = () => useModal(ConnectModal, { variant: 'REGULAR' });

type CredentialsType = {
  clientId: string;
  clientSecret: string;
};

const Form = createForm<CredentialsType>({ space: 0 });

const ConnectModal = () => {
  const { t } = useScopedTranslate(locale);
  const connectionMutation = useConnect({ onSuccess: hideModal });

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} icon={<IconBrandJourneys />} />
      <Form onSubmit={(data) => connectionMutation.mutateAsync(data)}>
        <Modal.Content>
          <Stack space={6}>
            <div>
              <Text font="regular14" text={t('insertKeys')} />
              <Stack space={1} orientation="horizontal">
                <Text font="regular14" text={t('findOrCreateToken')} />
                <ActionText.ExternalLink
                  href="https://journeys.50skills.app/settings/api"
                  font="regular14"
                >
                  {t('journeySettings')}
                </ActionText.ExternalLink>
              </Stack>
            </div>
            <Stack space={4}>
              <Form.TextField id="clientId" label={t('clientId')} validators={['required']} />
              <Form.TextField
                id="clientSecret"
                label={t('clientSecret')}
                validators={['required']}
              />
            </Stack>
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Button text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('connect')}
              variant="primary-blue"
              disabled={connectionMutation.isLoading}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Connect Journeys',
    insertKeys: 'Insert the keys for your company to connect.',
    findOrCreateToken: 'Find or create an API token in',
    journeySettings: 'Journey settings',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    connect: 'Connect',
  },
  is: {
    title: 'Tengja Journeys',
    insertKeys: 'Settu inn viðeigandi aðgangsupplýsingar til klára uppsetningu á tengingunni.',
    findOrCreateToken: 'Finndu eða stofnaður nýjan API lykil í',
    journeySettings: 'stillingum í Journeys',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    connect: 'Tengjast',
  },
};
